import './styles/AuthLayout.scss';

import { motion } from 'motion/react';
import { PropsWithChildren } from 'react';
import { Trans } from 'react-i18next';

import Logo from '../../assets/logo.svg';
import { THEME_IDS } from '../../constants';
import { cn } from '../../helpers/classBuilders';
import { useClassHelper } from '../../hooks';
import { CookieBanner } from '../CookieBannerURL';
import WidgetLayout from './component/WidgetLayout';

const componentName = 'c-AuthLayout';
const isWidgetMode = window.self !== window.top;

interface Props extends PropsWithChildren {
  logoUrl?: string;
  config?: Record<string, any>;
  imprintUrl?: string;
  className?: string;
}

export const AuthLayout = ({
  children,
  logoUrl,
  config,
  imprintUrl,
  className = '',
}: Props) => {
  const c = useClassHelper(componentName);

  const isGD = config?.context === THEME_IDS.GD;
  const isNL = config?.context === THEME_IDS.NETLYNC;
  const isCSG = config?.context === THEME_IDS.CSG;
  const isGSMA = config?.context === THEME_IDS.GSMA;

  const brandTitles: Record<string, string> = {
    [THEME_IDS.GSMA]: 'GSMA Entitlements',
    [THEME_IDS.CSG]: 'CSG Entitlements-as-a-Service',
    [THEME_IDS.GD]: 'AirOn360<sup>®</sup> ES',
    [THEME_IDS.NETLYNC]: 'NetLync ES',
  };

  const brandSubtitles = {
    [THEME_IDS.GSMA]: <>Entitlements-as-a-Service</>,
    [THEME_IDS.CSG]: <>Powered by Netlync</>,
    [THEME_IDS.GD]: <>Entitlements-as-a-Service</>,
    [THEME_IDS.NETLYNC]: (
      <>
        Entitlements-as-a-Service<sup>®</sup>
      </>
    ),
  };

  const getBrandTitle = () => {
    return brandTitles[config?.context ?? THEME_IDS.NETLYNC] ?? 'NetLync ES';
  };

  const getBrandSubtitle = () => {
    const defaultSubtitle = (
      <>
        Entitlements-as-a-Service<sup>®</sup>
      </>
    );

    return (
      brandSubtitles[config?.context ?? THEME_IDS.NETLYNC] ?? defaultSubtitle
    );
  };

  return isWidgetMode ? (
    <WidgetLayout config={config} className={className}>
      {children}
    </WidgetLayout>
  ) : (
    <main
      className={cn('flex h-full w-full overflow-hidden', c('base', className))}
    >
      <section className={c('__left-wrapper')}>
        <img className={c('__logo')} alt="logo" src={logoUrl || Logo} />
        <div
          className={cn(
            'flex flex-col px-[30px] pt-[280px] justify-center gap-2 2xl:gap-[1vw]',
            {
              'pt-[32vh]': isGSMA,
              'pt-[260px]': isCSG,
            },
          )}
        >
          <h1 className="text-5xl font-bold text-white leading-[3.5rem] tracking-[0.96px] 2xl:text-[4vw] 2xl:leading-[4vw]">
            <Trans i18nKey={getBrandTitle()} components={{ sup: <sup /> }} />
          </h1>
          <h2 className="text-2xl font-medium text-white leading-6 tracking-[0.48px] 2xl:text-[2vw]">
            {getBrandSubtitle()}
          </h2>
        </div>
        <CookieBanner imprintUrl={imprintUrl} />
      </section>
      <motion.aside
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.2,
          ease: 'easeInOut',
        }}
      >
        {children}
      </motion.aside>
    </main>
  );
};
